import { AbstractControl, UntypedFormGroup, ValidationErrors } from '@angular/forms';
import { SECOND_IN_MS, SECONDS_IN_MIN } from '../../../../../common/constants/date-config';
import { SchedulingType } from '../../../../../common/constants/scheduling-type';
import { getEndOfDay, getMinutesOfDay, getStartOfDay } from '../../../../../common/utils/date.helpers';

export enum CompartmentRuleTimeFrameEnum {
  StartTime = 'startTime',
  EndTime = 'endTime',
}

export function activityTypeValidator(): (activityTypesControl: AbstractControl) => ValidationErrors {
  return (activityTypesControl: AbstractControl) => {
    return activityTypesControl.value?.length ? null : { required: true };
  };
}

export function timeRangeValidator(key: CompartmentRuleTimeFrameEnum): (control: AbstractControl) => ValidationErrors {
  let previousStart = 0;
  let previousEnd = 0;

  return (control: AbstractControl): ValidationErrors => {
    const parent = control.parent as UntypedFormGroup;

    if (parent) {
      const { startTime, endTime } = parent.controls;
      const startTimeInMinutes: number = startTime.value && getMinutesOfDay(new Date(startTime.value));
      const endTimeInMinutes: number = endTime.value && getMinutesOfDay(new Date(endTime.value));

      if (
        key === CompartmentRuleTimeFrameEnum.StartTime &&
        startTime.value &&
        endTime.value &&
        startTimeInMinutes > endTimeInMinutes
      ) {
        const diff: number = previousStart
          ? getTimeFrameDiff(startTimeInMinutes, previousStart)
          : getTimeFrameDiff(startTimeInMinutes, endTimeInMinutes);

        let value: number = getValue(endTimeInMinutes, diff);

        if (getMinutesOfDay(new Date(value)) < endTimeInMinutes) {
          value = getEndOfDay();
        }

        endTime?.setValue(value);
      }

      if (
        key === CompartmentRuleTimeFrameEnum.EndTime &&
        endTime.value &&
        startTime.value &&
        endTimeInMinutes < startTimeInMinutes
      ) {
        const diff: number = previousEnd
          ? getTimeFrameDiff(previousEnd, endTimeInMinutes)
          : getTimeFrameDiff(startTimeInMinutes, endTimeInMinutes);

        let value: number = getValue(startTimeInMinutes, -diff);

        if (getMinutesOfDay(new Date(value)) > startTimeInMinutes) {
          value = getStartOfDay();
        }

        startTime?.setValue(value);
      }

      if (startTime.value && endTime.value && startTimeInMinutes === endTimeInMinutes) {
        const value = getValue(startTimeInMinutes, 0);
        endTime?.setValue(addMinutes(value, 1), { emitEvent: false });
      }

      previousStart = startTimeInMinutes;
      previousEnd = endTimeInMinutes;
    }

    return null;
  };
}

const getTimeFrameDiff = (start: number, end: number): number => {
  return Math.abs(start - end) * SECONDS_IN_MIN * SECOND_IN_MS;
};

const getValue = (minutes: number, diff: number): number => {
  return getStartOfDay() + minutes * SECONDS_IN_MIN * SECOND_IN_MS + diff;
};

const addMinutes = (start: number, minutes: number): number => {
  return start + minutes * SECONDS_IN_MIN * SECOND_IN_MS;
};

export function schedulingTypeValidator(control: AbstractControl): ValidationErrors {
  const value = control.value;
  const parent = control.parent as UntypedFormGroup;

  if (parent) {
    const { day } = parent.controls;
    if (value === SchedulingType.Weekly) {
      day.enable();
    } else {
      day.setValue([], { emitEvent: false });
      day.disable();
    }
  }

  return null;
}

export function sapValidator(
  deliveryAddressesControl: AbstractControl,
): (sapControl: AbstractControl) => ValidationErrors {
  return (sapControl: AbstractControl) => {
    const value = sapControl.value;
    const parent = sapControl.parent;
    if (parent && deliveryAddressesControl) {
      if (value && value?.length) {
        deliveryAddressesControl.clearValidators();
        deliveryAddressesControl.updateValueAndValidity({ emitEvent: false });

        if (!deliveryAddressesControl.value?.length) {
          deliveryAddressesControl.disable({ emitEvent: false });
        }
      } else {
        deliveryAddressesControl.enable();
        deliveryAddressesControl.updateValueAndValidity();
      }
    }

    return deliveryAddressesControl?.value?.length && sapControl?.value?.length
      ? { deliveryAddressOrAddressPointShouldBeEmpty: true }
      : null;
  };
}
